import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, SlidersHorizontal, Grid, List, ArrowUpDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ProductCard from '../components/product/ProductCard';
import { productsApi } from '../services/api';
import { useLocalization } from '../contexts/LocalizationContext';
import SEOHead from '../components/SEOHead';

const SearchResultsPage = () => {
  const { t, i18n } = useTranslation();
  const { countryCode } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('relevance');
  const [totalResults, setTotalResults] = useState(0);

  const currentLang = i18n.language || 'es';

  useEffect(() => {
    if (query && countryCode) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [query, countryCode]);

  // Helper function to normalize text for accent-insensitive search
  const normalizeText = (text) => {
    return text
      .normalize('NFD') // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .toLowerCase();
  };

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    try {
      // Search with country parameter to get flattened product data
      const response = await productsApi.getAll({ 
        page: 1, 
        limit: 50,
        country: countryCode
      });
      const allProducts = response.products || [];
      
      // Filter products based on search term (accent-insensitive)
      const searchNormalized = normalizeText(searchTerm);
      const filtered = allProducts.filter(product => {
        // Products are already flattened - name and description are strings
        const name = normalizeText(product.name || '');
        const description = normalizeText(product.description || '');
        const shortDescription = normalizeText(product.shortDescription || '');
        const tags = product.tags || [];
        const sku = normalizeText(product.sku || '');
        
        return (
          name.includes(searchNormalized) ||
          description.includes(searchNormalized) ||
          shortDescription.includes(searchNormalized) ||
          tags.some(tag => normalizeText(tag).includes(searchNormalized)) ||
          sku.includes(searchNormalized)
        );
      });
      
      setProducts(filtered);
      setTotalResults(filtered.length);
    } catch (error) {
      console.error('Search error:', error);
      setProducts([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
    }
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    let sorted = [...products];
    
    switch (value) {
      case 'price-asc':
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'name':
        sorted.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        // relevance - keep current order
        break;
    }
    
    setProducts(sorted);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={query ? `${t('common.search')}: ${query}` : t('common.search')}
        url={query ? `/search?q=${encodeURIComponent(query)}` : '/search'}
      />
      {/* Search Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('search.placeholder') || 'Search products...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 h-14 text-lg rounded-full border-2 focus:border-[#be1f1a] text-gray-900 placeholder:text-gray-500"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#be1f1a] hover:bg-[#a71b17] rounded-full"
              >
                {t('search.search') || 'Search'}
              </Button>
            </div>
          </form>

          {/* Results Info */}
          {query && (
            <div className="text-center">
              <p className="text-gray-900 font-medium text-lg">
                {loading ? (
                  <span>{t('search.searching') || 'Searching'}...</span>
                ) : (
                  <span>
                    {totalResults} {t('search.resultsFor') || 'results for'}{' '}
                    <strong className="text-black font-bold">"{query}"</strong>
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Toolbar */}
        {!loading && products.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-white rounded-lg p-4 shadow-sm">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${
                  viewMode === 'grid'
                    ? 'bg-[#be1f1a] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${
                  viewMode === 'list'
                    ? 'bg-[#be1f1a] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <List className="h-5 w-5" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t('shop.sortBy') || 'Sort by'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">
                    {t('shop.relevance') || 'Relevance'}
                  </SelectItem>
                  <SelectItem value="newest">
                    {t('shop.newest') || 'Newest'}
                  </SelectItem>
                  <SelectItem value="price-asc">
                    {t('shop.priceLowToHigh') || 'Price: Low to High'}
                  </SelectItem>
                  <SelectItem value="price-desc">
                    {t('shop.priceHighToLow') || 'Price: High to Low'}
                  </SelectItem>
                  <SelectItem value="name">
                    {t('shop.name') || 'Name A-Z'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-[#be1f1a]"></div>
            <p className="mt-4 text-gray-900 font-medium text-lg">{t('common.loading') || 'Loading'}...</p>
          </div>
        )}

        {/* No Results */}
        {!loading && products.length === 0 && query && (
          <div className="text-center py-20">
            <Search className="h-24 w-24 text-gray-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('search.noResults') || 'No products found'}
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              {t('search.noResultsDescription') || 'Try searching with different keywords'}
            </p>
            <Link to="/shop">
              <Button className="bg-[#be1f1a] hover:bg-[#a71b17] text-white font-semibold">
                {t('search.browseAll') || 'Browse All Products'}
              </Button>
            </Link>
          </div>
        )}

        {/* Products Grid */}
        {!loading && products.length > 0 && (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                : 'flex flex-col gap-4'
            }
          >
            {products.map((product) => (
              <ProductCard key={product._id || product.id} product={product} />
            ))}
          </div>
        )}

        {/* No Query */}
        {!query && (
          <div className="text-center py-20">
            <Search className="h-24 w-24 text-gray-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('search.startSearching') || 'Start searching'}
            </h2>
            <p className="text-lg text-gray-700">
              {t('search.startSearchingDescription') || 'Enter a keyword to find products'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
